import type { DirecaoDaOrdem, OrdemDaEntrada } from '@/api/chamados'

/**
 * A ordem da lista da Entrada (spec 2026-10-21 §2.2). Mora na URL (`?ordem=&dir=`); sem ela, a
 * ordem padrão do servidor (negrito primeiro, depois a maior espera).
 */
export interface Ordem {
  coluna: OrdemDaEntrada
  direcao: DirecaoDaOrdem
}

export const ORDENS: readonly OrdemDaEntrada[] = ['id', 'solicitante', 'responsavel', 'prioridade', 'espera']

/** O 1º clique: o sentido que se espera da coluna (`chamados.entrada.SENTIDO_NATURAL`). */
export const SENTIDO_NATURAL: Record<OrdemDaEntrada, DirecaoDaOrdem> = {
  id: 'desc', solicitante: 'asc', responsavel: 'asc', prioridade: 'desc', espera: 'desc',
}

const inverso = (d: DirecaoDaOrdem): DirecaoDaOrdem => (d === 'asc' ? 'desc' : 'asc')

export function lerOrdem(params: URLSearchParams): Ordem | null {
  const coluna = params.get('ordem')
  if (!coluna || !(ORDENS as readonly string[]).includes(coluna)) return null
  const dir = params.get('dir')
  const c = coluna as OrdemDaEntrada
  return { coluna: c, direcao: dir === 'asc' || dir === 'desc' ? dir : SENTIDO_NATURAL[c] }
}

/** A URL com a ordem; `null` tira `ordem` e `dir`. O resto da URL (pasta, org, fila) fica. */
export function comOrdem(params: URLSearchParams, ordem: Ordem | null): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('ordem')
  next.delete('dir')
  if (ordem) {
    next.set('ordem', ordem.coluna)
    next.set('dir', ordem.direcao)
  }
  return next
}

/** O clique no cabeçalho: 1º no sentido natural, 2º inverte, 3º volta ao padrão. Outra coluna
 *  recomeça no sentido natural dela. */
export function proximaOrdem(atual: Ordem | null, coluna: OrdemDaEntrada): Ordem | null {
  const natural = SENTIDO_NATURAL[coluna]
  if (!atual || atual.coluna !== coluna) return { coluna, direcao: natural }
  if (atual.direcao === natural) return { coluna, direcao: inverso(natural) }
  return null
}

/** Como cada coluna diz o sentido, por extenso: no nome acessível do cabeçalho e no seletor. */
const SENTIDOS: Record<OrdemDaEntrada, Record<DirecaoDaOrdem, string>> = {
  id: { desc: 'mais novo', asc: 'mais antigo' },
  solicitante: { asc: 'A–Z', desc: 'Z–A' },
  responsavel: { asc: 'A–Z', desc: 'Z–A' },
  prioridade: { desc: 'mais urgente', asc: 'menos urgente' },
  espera: { desc: 'maior', asc: 'menor' },
}

export const ROTULO_DA_COLUNA: Record<OrdemDaEntrada, string> = {
  id: 'Número', solicitante: 'Solicitante', responsavel: 'Responsável', prioridade: 'Prioridade',
  espera: 'Espera',
}

export const sentidoPorExtenso = (o: Ordem) => SENTIDOS[o.coluna][o.direcao]

const PADRAO = 'padrao'

/** A ordem que vale na pasta: Responsável só onde há a coluna (spec 2026-10-22 §3.2). Trocar
 *  de pasta pela barra já zera a ordem; isto cobre a URL que chega de fora (link, favorito). */
export const ordemNaPasta = (ordem: Ordem | null, comResponsavel: boolean): Ordem | null =>
  ordem?.coluna === 'responsavel' && !comResponsavel ? null : ordem

/** As escolhas do seletor de tela estreita: a padrão e as colunas da pasta nos dois sentidos. */
export const escolhasDeOrdem = (comResponsavel: boolean): { value: string; label: string }[] => [
  { value: PADRAO, label: 'Padrão (não lidos primeiro)' },
  ...ORDENS.filter((c) => comResponsavel || c !== 'responsavel').flatMap((coluna) => {
    const natural = SENTIDO_NATURAL[coluna]
    return [natural, inverso(natural)].map((direcao) => ({
      value: `${coluna}:${direcao}`,
      label: `${ROTULO_DA_COLUNA[coluna]}: ${SENTIDOS[coluna][direcao]}`,
    }))
  }),
]

export const valorDaEscolha = (o: Ordem | null) => (o ? `${o.coluna}:${o.direcao}` : PADRAO)

export function ordemDaEscolha(valor: string | null): Ordem | null {
  if (!valor || valor === PADRAO) return null
  const [coluna, direcao] = valor.split(':')
  return lerOrdem(new URLSearchParams({ ordem: coluna, dir: direcao }))
}
